import React from 'react';
import { BoldBodyText, Card, Grid, SmallBodyText } from 'ui-toolkit';
import styled from 'styled-components';
import { IAPIFeeData } from '../../../types/clientPortal/Fees/IAPIFee';
import { ISSFee } from '../../../ui-toolkit/settlementStatement/ISSFee';
import { FeeTable } from './FeeTable';
import { FeeTextComponent } from './FeeTextComponent';
import { FeeAmount } from './FeeAmount';
import { eRowPresentationStyle } from '../../../types/clientPortal/Fees/eRowPresentationStyle';
import { generateTotalsForIAPIFeeData } from '../../../ui-toolkit/cdItems/data/generateTotals';
import { eTransactionCategory } from '../../../ui-toolkit/enums/eTransactionType';
import { SSSection } from '../../../ui-toolkit/settlementStatement/SSSection';
import { eSSSection } from '../../../ui-toolkit/settlementStatement/eSSSection';
import { getSSSectionLabel } from '../../../ui-toolkit/settlementStatement/GetSSSectionLabel';
import { getSSSectionOrder } from '../../../ui-toolkit/settlementStatement/getSSSectionOrder';
import { getSSSectionsForIAPIFeeData } from '../../../ui-toolkit/settlementStatement/getSSSections';

const SmallItalicsBodyText = styled(SmallBodyText)`
  font-style: italic;
`;

interface IFeesProps {
  fees: IAPIFeeData[];
  transactionCategory: eTransactionCategory;
  discountApplied?: boolean;
  salesTaxMessage?: boolean;
}

export const Fees: React.FC<IFeesProps> = (props) => {
  const sums = generateTotalsForIAPIFeeData(props.fees);
  const subtotal: SSSection = {
    id: 'totals',
    type: eSSSection.totals,
    label: getSSSectionLabel(eSSSection.totals),
    order: getSSSectionOrder(eSSSection.totals, props.transactionCategory),
    buyerAtClosing: sums.buyer_at_closing,
    buyerBeforeClosing: sums.buyer_before_closing,
    sellerAtClosing: sums.seller_at_closing,
    sellerBeforeClosing: sums.seller_before_closing,
    paidByOthersTotal: sums.paid_by_others,
    items: [],
  };

  const borrowerName = props.transactionCategory === eTransactionCategory.Refinance ? 'BORROWER' : 'BUYER';

  const sections: SSSection[] = [...getSSSectionsForIAPIFeeData(props.fees, props.transactionCategory), subtotal];

  const tableData: any = [];
  sections
    .sort((a, b) => sortFee(a, b))
    .forEach((s) => {
      tableData.push({
        ...s,
        style: eRowPresentationStyle.header,
      });

      s.items
        .sort((a, b) => sortFee(a, b))
        .forEach((i) => {
          tableData.push({
            ...i,
            style: eRowPresentationStyle.item,
          });
        });
    });

  const borrowerColumns = [
    {
      dataField: 'label',
      formatter: (cell: any, item: any) => (
        <FeeTextComponent style={item.style}>
          {item.style === eRowPresentationStyle.header ? item.label : item.ssLabel}
        </FeeTextComponent>
      ),
      text: 'FEE TYPE',
    },
    {
      dataField: 'buyerAtClosing',
      formatter: (cell: number | null, item: any) => <FeeAmount style={item.style} value={cell} />,
      text: 'At closing',
    },
    {
      dataField: 'buyerBeforeClosing',
      formatter: (cell: number | null, item: any) => <FeeAmount style={item.style} value={cell} />,
      text: `${borrowerName} PAID \n Before closing`,
    },
  ];
  const paidByOther = {
    dataField: 'paidByOthersTotal',
    formatter: (cell: number | null, item: any) => <FeeAmount style={item.style} value={cell} />,
    text: 'PAID BY OTHERS',
  };
  const sellerColumns = [
    {
      dataField: 'sellerAtClosing',
      formatter: (cell: number | null, item: any) => <FeeAmount style={item.style} value={cell} />,
      text: 'At closing',
    },
    {
      dataField: 'sellerBeforeClosing',
      formatter: (cell: number | null, item: any) => <FeeAmount style={item.style} value={cell} />,
      text: `SELLER PAID \n Before closing`,
    },
  ];

  const columns =
    props.transactionCategory === eTransactionCategory.Refinance
      ? [...borrowerColumns, paidByOther]
      : [...borrowerColumns, ...sellerColumns, paidByOther];

  return (
    <Card title={'Fees'}>
      <Grid>
        {props.discountApplied ? <BoldBodyText>Discount Applied</BoldBodyText> : null}
        <FeeTable columns={columns as any} data={tableData} className={'lender-fee-table'} keyField="id" />
        {props.salesTaxMessage && (
          <SmallItalicsBodyText>Sales tax not included, this will be charged at the local rate</SmallItalicsBodyText>
        )}
      </Grid>
    </Card>
  );
};

const sortFee = (a: ISSFee | SSSection, b: ISSFee | SSSection): number => {
  if (a.order > b.order) return 1;
  else if (a.order < b.order) return -1;
  else if (a.createdAt && b.createdAt) {
    if (a.createdAt > b.createdAt) return 1;
    else if (a.createdAt < b.createdAt) return -1;
    else return 0;
  } else {
    return 0;
  }
};
